// ... imports ...
import React, { useState, useEffect } from 'react';
import {
    ScatterChart,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Sankey
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";

// Constants
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// --- Interfaces ---
interface DevOpsSummary {
    kpi: {
        latencyP99: number;
        errorRate: number;
        cacheHitRate: number;
    };
    sankey: {
        nodes: Array<{ name: string }>;
        links: Array<{ source: number; target: number; value: number }>;
    };
    scatter: Array<{ x: number; y: number; type: string }>;
}

// --- Mock Data ---
const MOCK_DATA: DevOpsSummary = {
    kpi: {
        latencyP99: 240,
        errorRate: 0.0002, // 0.02%
        cacheHitRate: 0.42 // 42%
    },
    sankey: {
        nodes: [
            { name: 'Mobile App' }, { name: 'Web Chatbot' }, { name: 'Internal Slack' }, // 0-2
            { name: 'PII Filter' }, { name: 'Cache Layer' }, // 3-4
            { name: 'Model Selector' }, // 5
            { name: 'OpenAI' }, { name: 'GPT-5.2-Pro' }, { name: 'SigLLM-Local' }, { name: 'Memory (Cache)' } // 6-9
        ],
        links: [
            { source: 0, target: 3, value: 4500 }, { source: 1, target: 3, value: 3200 }, { source: 2, target: 3, value: 1800 },
            { source: 3, target: 4, value: 9500 },
            { source: 4, target: 9, value: 3990 }, { source: 4, target: 5, value: 5510 },
            { source: 5, target: 6, value: 3000 }, { source: 5, target: 7, value: 1500 }, { source: 5, target: 8, value: 1010 },
        ]
    },
    scatter: [
        ...Array.from({ length: 30 }, () => ({ x: Math.floor(Math.random() * 100) + 20, y: Number((Math.random() * 0.001).toFixed(4)), type: 'SigLLM-Local' })),
        ...Array.from({ length: 20 }, () => ({ x: Math.floor(Math.random() * 300) + 200, y: Number((Math.random() * 0.005 + 0.002).toFixed(4)), type: 'GPT-5.2-Pro' })),
        ...Array.from({ length: 15 }, () => ({ x: Math.floor(Math.random() * 600) + 500, y: Number((Math.random() * 0.03 + 0.01).toFixed(4)), type: 'GPT-5.2' })),
    ]
};

const DevOpsPage: React.FC = () => {
    const token = localStorage.getItem("token");
    const [isDemoMode, setIsDemoMode] = useState<boolean>(false);
    const [data, setData] = useState<DevOpsSummary>(MOCK_DATA);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        if (isDemoMode) {
            setData(MOCK_DATA);
            return;
        }

        const fetchData = async () => {
            setIsLoading(true);
            try {
                const response = await fetch(`${API_URL}/metrics/devops/summary`, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });

                if (response.ok) {
                    const realData = await response.json();
                    // Basic validation/fallback
                    if (!realData.kpi) realData.kpi = MOCK_DATA.kpi;
                    if (!realData.sankey) realData.sankey = { nodes: [], links: [] };
                    if (!realData.scatter) realData.scatter = [];

                    setData(realData);
                } else {
                    console.error("Failed to fetch DevOps metrics");
                }
            } catch (error) {
                console.error("Error fetching DevOps metrics:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [isDemoMode, token]);

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            <div className="flex flex-row items-center justify-between">
                <div className="flex flex-col gap-2">
                    <h2 className="text-2xl font-bold tracking-tight">DevOps Health & Routing</h2>
                    <p className="text-muted-foreground">Real-time system performance and traffic flow analysis.</p>
                </div>
                <div className="flex items-center space-x-2">
                    <Switch
                        id="demo-mode"
                        checked={isDemoMode}
                        onCheckedChange={setIsDemoMode}
                    />
                    <Label htmlFor="demo-mode">Demo Mode</Label>
                </div>
            </div>

            {/* Global KPI Header */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Avg Latency (P99)</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-emerald-500">
                            {isLoading ? "..." : `${data.kpi.latencyP99}ms`}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-foreground">
                            {isLoading ? "..." : `${(data.kpi.errorRate * 100).toFixed(2)}%`}
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                        <CardTitle className="text-sm font-medium">Cache Hit Rate</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="text-2xl font-bold text-sky-500">
                            {isLoading ? "..." : `${(data.kpi.cacheHitRate * 100).toFixed(2)}%`}
                        </div>
                    </CardContent>
                </Card>
            </div>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                {/* Route Efficiency Heatmap (Sankey) */}
                <Card className="flex flex-col min-h-[500px]">
                    <CardHeader>
                        <CardTitle>Traffic Router Flow</CardTitle>
                        <CardDescription>Visualizing request complexity: Source → Router → Destination</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1">
                        {data.sankey.nodes.length > 0 && data.sankey.links.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <Sankey
                                    data={data.sankey}
                                    node={{ stroke: '#10b981', strokeWidth: 0 }}
                                    nodePadding={40}
                                    margin={{ left: 10, right: 10, top: 10, bottom: 10 }}
                                    link={{ stroke: '#3b82f6', opacity: 0.3 }}
                                >
                                    <Tooltip
                                        contentStyle={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))', borderRadius: '8px', color: 'hsl(var(--card-foreground))' }}
                                        itemStyle={{ color: 'hsl(var(--card-foreground))' }}
                                    />
                                </Sankey>
                            </ResponsiveContainer>
                        ) : (
                            <div className="flex items-center justify-center h-full min-h-[300px] text-muted-foreground w-full">
                                No traffic flow data available
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Latency vs. Cost Matrix (Scatter) */}
                <Card className="flex flex-col min-h-[500px]">
                    <CardHeader>
                        <CardTitle>Latency vs. Cost Matrix</CardTitle>
                        <CardDescription>Optimization Goal: Clustering in Bottom-Left</CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 relative">
                        {data.scatter.length === 0 && (
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
                                <span className="text-muted-foreground bg-background/80 px-4 py-2 rounded-md">No data available</span>
                            </div>
                        )}
                        <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart
                                margin={{ top: 20, right: 20, bottom: 20, left: 0 }}
                            >
                                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground))" opacity={0.2} />
                                <XAxis
                                    type="number"
                                    dataKey="x"
                                    name="Latency"
                                    unit="ms"
                                    domain={data.scatter.length > 0 ? ['auto', 'auto'] : [0, 1000]}
                                    stroke="hsl(var(--muted-foreground))"
                                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    type="number"
                                    dataKey="y"
                                    name="Cost"
                                    unit="$"
                                    domain={data.scatter.length > 0 ? ['auto', 'auto'] : [0, 0.05]}
                                    stroke="hsl(var(--muted-foreground))"
                                    tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                                    axisLine={false}
                                    tickLine={false}
                                    width={60}
                                />
                                <Tooltip
                                    cursor={{ strokeDasharray: '3 3' }}
                                    content={({ active, payload }: any) => {
                                        if (active && payload && payload.length) {
                                            const point = payload[0].payload;
                                            return (
                                                <div className="bg-card border border-border p-3 rounded-lg shadow-xl text-xs">
                                                    <p className="text-card-foreground font-medium mb-1">{point.type}</p>
                                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                                                        <span className="text-muted-foreground">Latency:</span>
                                                        <span className="text-emerald-500 text-right">{point.x}ms</span>
                                                        <span className="text-muted-foreground">Cost:</span>
                                                        <span className="text-amber-500 text-right">${point.y.toFixed(6)}</span>
                                                    </div>
                                                </div>
                                            );
                                        }
                                        return null;
                                    }}
                                />
                                <Scatter name="Requests" data={data.scatter} fill="#10b981" fillOpacity={0.6} />
                            </ScatterChart>
                        </ResponsiveContainer>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default DevOpsPage;
